import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import ImpersonationBanner from './ImpersonationBanner';
import { authAPI } from '../utils/api';

// ── Nav items ─────────────────────────────────────────────────────────────────
const navItems = [
  { path: '/dashboard',      label: 'Dashboard',      icon: DashIcon,    roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/clients',        label: 'Clients',         icon: ClientIcon,  roles: ['SuperAdmin','RecruiterManager','Developer'] },
  { path: '/requirements',   label: 'Requirements',    icon: ReqIcon,     roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/candidates',     label: 'Candidates',      icon: CandIcon,    roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/interviews',     label: 'Interviews',      icon: InterviewIcon, roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/submissions',    label: 'Submissions',     icon: SubmissionsIcon, roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/tasks',          label: 'Tasks',           icon: TaskIcon,    roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/teams',          label: 'Teams',           icon: TeamIcon,    roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/team-dashboard', label: 'Team Dashboard',  icon: TrophyIcon,  roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/emails',         label: 'Emails',          icon: EmailIcon,   roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/reports',        label: 'Reports',         icon: ReportIcon,  roles: ['SuperAdmin','RecruiterManager','Developer'] },
  { path: '/users',          label: 'Users',           icon: UsersIcon,   roles: ['SuperAdmin'] },
  // Impersonate sidebar entry — only for non-SuperAdmin roles. SuperAdmin
  // already has Impersonate buttons on the Users page and doesn't need a
  // separate destination. Layout further hides this item for non-SuperAdmin
  // users who have no active grants.
  { path: '/impersonate',    label: 'Impersonate',     icon: UsersIcon,   roles: ['RecruiterManager','Recruiter','Developer'] },
  { path: '/activity-logs',  label: 'Activity Logs',   icon: LogIcon,     roles: ['SuperAdmin'] },
  { path: '/schedulers',     label: 'Schedulers',      icon: SchedulerIcon, roles: ['SuperAdmin'] },
  { path: '/server-logs',    label: 'Server Logs',     icon: TerminalIcon, roles: ['SuperAdmin'] },
  { path: '/candidate-finder', label: 'Candidate Finder', icon: FinderIcon, roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/ctc-calculator',   label: 'CTC Calculator',   icon: CTCIcon,    roles: ['SuperAdmin','RecruiterManager','Recruiter','Developer'] },
  { path: '/settings',       label: 'Settings',        icon: SettingsIcon, roles: ['SuperAdmin'] },
];

// Role pill colors — theme-aware
const roleColorsDark = {
  SuperAdmin:       { bg: 'rgba(99,102,241,0.15)', text: '#a5b4fc', border: 'rgba(99,102,241,0.3)' },
  RecruiterManager: { bg: 'rgba(6,182,212,0.12)',  text: '#67e8f9', border: 'rgba(6,182,212,0.3)' },
  Recruiter:        { bg: 'rgba(16,185,129,0.12)', text: '#6ee7b7', border: 'rgba(16,185,129,0.3)' },
  Developer:        { bg: 'rgba(245,158,11,0.15)', text: '#fcd34d', border: 'rgba(245,158,11,0.3)' },
};
const roleColorsLight = {
  SuperAdmin:       { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE' },
  RecruiterManager: { bg: '#CFFAFE', text: '#0E7490', border: '#A5F3FC' },
  Recruiter:        { bg: '#D1FAE5', text: '#15803D', border: '#A7F3D0' },
  Developer:        { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
};

// ── SVG Icons ─────────────────────────────────────────────────────────────────
function Icon({ d, d2, children, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {d && <path d={d} />}
      {d2 && <path d={d2} />}
      {children}
    </svg>
  );
}

function DashIcon()   { return <Icon><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></Icon>; }
function ClientIcon() { return <Icon d="M3 21V7a2 2 0 012-2h14a2 2 0 012 2v14M9 21V12h6v9" d2="M3 10h18" />; }
function CandIcon()   { return <Icon><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></Icon>; }
function ReqIcon()    { return <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h4" />; }
function InterviewIcon() { return <Icon><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M8 18h.01M12 18h.01M16 14h.01"/></Icon>; }
function TaskIcon()   { return <Icon><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></Icon>; }
function TeamIcon()   { return <Icon><circle cx="9" cy="7" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="18" cy="8" r="2.5"/><path d="M16 20c0-2.8 1.8-5 5-5"/></Icon>; }
function TrophyIcon() { return <Icon d="M8 21h8M12 17v4M7 4H4l1 6c0 3.3 2.7 6 7 6s7-2.7 7-6l1-6h-3M4 10H2M22 10h-2" />; }
function EmailIcon()  { return <Icon><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 8l10 6 10-6"/></Icon>; }
function ReportIcon() { return <Icon><path d="M18 20V10M12 20V4M6 20v-6"/></Icon>; }
function UsersIcon()  { return <Icon><circle cx="9" cy="7" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="7" r="3"/><path d="M21 20c0-3.3-2.7-6-6-6"/></Icon>; }
function LogIcon()    { return <Icon><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/></Icon>; }
function SubmissionsIcon() { return <Icon><path d="M9 12l2 2 4-4"/><path d="M20 6H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2z"/><path d="M16 2v4M8 2v4"/></Icon>; }
function FinderIcon()   { return <Icon><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></Icon>; }
function CTCIcon()      { return <Icon><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></Icon>; }
function SchedulerIcon() { return <Icon><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5"/><path d="M5 3L2 6M19 3l3 3"/></Icon>; }
function TerminalIcon() { return <Icon><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 9l3 3-3 3M12 15h5"/></Icon>; }
function SettingsIcon() { return <Icon><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></Icon>; }
function MenuIcon()   { return <Icon d="M3 12h18M3 6h18M3 18h18" />; }
function SignOutIcon(){ return <Icon d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />; }
function SunIcon()    { return <Icon><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></Icon>; }
function MoonIcon()   { return <Icon d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />; }
function ChevronsLeftIcon() { return <Icon d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />; }
function PanelLeftIcon()    { return <Icon><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/></Icon>; }

// ── Theme toggle button ───────────────────────────────────────────────────────
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  return (
    <button
      onClick={toggleTheme}
      aria-label={`Switch to ${isLight ? 'dark' : 'light'} mode`}
      title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
      className="flex items-center justify-center w-9 h-9 rounded-xl transition-all"
      style={{
        color: 'var(--text-tertiary)',
        background: 'var(--surface-3)',
        border: '1px solid var(--border-subtle)',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
    >
      {isLight ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ visibleNav, user, onClose, onLogout, onCollapse }) {
  const location = useLocation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const rc = (isLight ? roleColorsLight : roleColorsDark)[user?.role] || (isLight ? roleColorsLight : roleColorsDark).Recruiter;
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside
      className="w-64 h-full flex flex-col relative overflow-hidden"
      style={{
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--border-subtle)',
      }}
    >
      {/* Subtle ambient glow top — dimmed in light mode */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 pointer-events-none"
        style={{
          background: isLight
            ? 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }} />

      {/* ── Logo ── */}
      <div className="relative px-5 pt-6 pb-5" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 relative"
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(139,92,246,0.25) 100%)',
              border: '1px solid rgba(99,102,241,0.35)',
              boxShadow: isLight ? '0 2px 8px rgba(99,102,241,0.15)' : '0 0 20px rgba(99,102,241,0.25)',
            }}
          >
            <span className="font-display font-black text-lg" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1,
            }}>A</span>
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-xl leading-none" style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.03em',
            }}>Aurx</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', letterSpacing: '0.08em' }}>
              Recruit Platform
            </div>
          </div>

          {/* Collapse sidebar — desktop only (mobile uses the hamburger drawer) */}
          {onCollapse && (
            <button
              onClick={onCollapse}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              className="hidden lg:flex items-center justify-center w-7 h-7 rounded-lg ml-auto flex-shrink-0 transition-all"
              style={{ color: 'var(--text-tertiary)', background: 'var(--surface-3)', border: '1px solid var(--border-subtle)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            >
              <ChevronsLeftIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleNav.map((item, idx) => {
          const active = location.pathname.startsWith(item.path);
          const IconComp = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              style={{ animationDelay: `${idx * 30}ms` }}
              className={`nav-item animate-fade-up flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium relative group ${active ? 'active' : ''}`}
            >
              <span
                className="flex-shrink-0 transition-all duration-200"
                style={{
                  color: active ? 'var(--accent-text)' : 'var(--text-muted)',
                  filter: active && !isLight ? 'drop-shadow(0 0 6px rgba(99,102,241,0.6))' : 'none',
                }}
              >
                <IconComp />
              </span>
              <span style={{ color: active ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                {item.label}
              </span>
              {!active && (
                <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ background: 'var(--hover-bg)', zIndex: -1 }} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* ── User card ── */}
      <div className="px-3 pb-4 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <div
          className="rounded-xl p-3 mb-2"
          style={{
            background: 'var(--surface-3)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: isLight ? '0 2px 6px rgba(99,102,241,0.2)' : '0 0 12px rgba(99,102,241,0.4)',
                color: '#fff',
              }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" title={user?.name} style={{ color: 'var(--text-secondary)' }}>{user?.name}</div>
              <div className="text-xs truncate" title={user?.email} style={{ color: 'var(--text-muted)' }}>{user?.email}</div>
            </div>
          </div>
          <div className="mt-2.5">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}
            >
              {user?.role}
            </span>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 group"
          style={{ color: 'var(--text-muted)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <SignOutIcon />
          Sign Out
        </button>
      </div>
    </aside>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = document.querySelector('main');
    if (!el) return;
    const fn = () => setScrolled(el.scrollTop > 10);
    el.addEventListener('scroll', fn);
    return () => el.removeEventListener('scroll', fn);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Collapse/expand the desktop sidebar (Bitbucket-style). Persisted so it sticks.
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Show the "Impersonate" sidebar entry only when the current user actually
  // has at least one grant. SuperAdmin gets it too (the /grants/mine endpoint
  // returns superAdmin:true) — they typically use the Users page but the
  // entry is still legitimate.
  const [hasImpersonationAccess, setHasImpersonationAccess] = useState(false);
  useEffect(() => {
    if (!user) { setHasImpersonationAccess(false); return; }
    let cancelled = false;
    authAPI.listMyImpersonationGrants()
      .then(({ data }) => {
        if (cancelled) return;
        const access = !!data?.superAdmin || (Array.isArray(data?.users) && data.users.length > 0);
        setHasImpersonationAccess(access);
      })
      .catch(() => { if (!cancelled) setHasImpersonationAccess(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const visibleNav = navItems
    .filter(item => item.roles.includes(user?.role))
    .filter(item => item.path !== '/impersonate' || hasImpersonationAccess);
  const pageTitle = navItems.find(n => location.pathname.startsWith(n.path))?.label || 'Dashboard';

  return (
    <div className="flex app-shell-h overflow-hidden" style={{ background: 'var(--bg-base)' }}>
      <div className={`flex-shrink-0 app-shell-h sticky top-0 ${sidebarCollapsed ? 'hidden' : 'hidden lg:block'}`}>
        <Sidebar visibleNav={visibleNav} user={user} onClose={() => {}} onLogout={handleLogout} onCollapse={toggleSidebarCollapsed} />
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 backdrop-blur-sm"
            style={{ background: isLight ? 'rgba(15,23,42,0.4)' : 'rgba(2,6,23,0.8)' }}
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative w-64 h-full animate-slide-in-left">
            <Sidebar visibleNav={visibleNav} user={user} onClose={() => setSidebarOpen(false)} onLogout={handleLogout} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 app-shell-h overflow-hidden">
        <ImpersonationBanner />
        <header
          className="sticky top-0 z-40 transition-all duration-300 px-4 lg:px-8 py-3"
          style={{
            background: scrolled ? 'var(--topbar-bg-scrolled)' : 'var(--topbar-bg)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderBottom: scrolled ? '1px solid var(--border-subtle)' : '1px solid transparent',
          }}
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            <button
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-xl transition-all"
              style={{ color: 'var(--text-tertiary)', background: 'var(--surface-3)' }}
              onClick={() => setSidebarOpen(true)}
            >
              <MenuIcon />
            </button>

            {/* Show sidebar — desktop only, appears when the sidebar is collapsed */}
            {sidebarCollapsed && (
              <button
                className="hidden lg:flex items-center justify-center w-9 h-9 rounded-xl transition-all"
                style={{ color: 'var(--text-tertiary)', background: 'var(--surface-3)' }}
                onClick={toggleSidebarCollapsed}
                title="Show sidebar"
                aria-label="Show sidebar"
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
              >
                <PanelLeftIcon />
              </button>
            )}

            <div className="hidden sm:flex items-center gap-2 min-w-0">
              <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Aurx</span>
              <span style={{ color: 'var(--text-faint)' }}>/</span>
              <span className="text-sm font-semibold truncate min-w-0" style={{ color: 'var(--text-secondary)' }}>{pageTitle}</span>
            </div>

            <div className="flex-1" />

            <ThemeToggle />

            <div style={{ color: 'var(--text-tertiary)' }}>
              <NotificationBell />
            </div>

            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold cursor-pointer transition-all"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: isLight ? '0 2px 6px rgba(99,102,241,0.2)' : '0 0 12px rgba(99,102,241,0.35)',
                color: '#fff',
              }}
              title={user?.name}
            >
              {(user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>

        <main
          className="flex-1 overflow-y-auto overflow-x-hidden page-enter"
          style={{ background: 'transparent', overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="fixed inset-0 pointer-events-none z-0" style={{
            backgroundImage: `
              linear-gradient(var(--grid-line) 1px, transparent 1px),
              linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            top: 0, left: 0, right: 0, bottom: 0,
          }} />
          <div className="relative z-10 p-3 sm:p-4 lg:p-6 max-w-full min-w-0 app-scroll-pad-bottom">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
